import { ActionIcon, Alert, Box, Button, Card, CopyButton, Grid, Group, Menu, PasswordInput, Select, SimpleGrid, Stack, Text, TextInput, Title, Tooltip } from '@mantine/core';
import { IconArchive, IconCheck, IconCopy, IconDots, IconExternalLink, IconKey, IconPencil, IconPlayerPause, IconPlayerPlay } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import type { FormSummary, OrganizationDetails } from '../api/types';
import { OrgLogo } from '../components/org-logo';
import { PageActions } from '../components/page-actions';
import { HealthDot, SubscriptionCard, SubscriptionChip } from '../components/subscription';
import { useToast } from '../components/Toast';
import { nameOf, tr } from '../lib/i18n';
import { fmtAgo, fmtDate, fmtNum, ORG_STATUS } from '../lib/labels';
import { ConfirmModal, ErrorState, Field, Kpi, Loading, Panel, StatusBadge, toneOf } from '../ui/kit';
import { OrganizationFormDialog } from './OrganizationFormDialog';
import { OrgMapSettings } from '../components/OrgMapSettings';

type Action = 'suspend' | 'activate' | 'archive';
const ACTIONS: Record<Action, { label: string; text: string; danger?: boolean }> = {
  suspend: {
    label: tr('تعليق الشركة', 'Suspend company'),
    text: tr('سيتم إيقاف دخول كل موظفي الشركة وإيقاف رابط النموذج العام حتى إعادة التفعيل.', 'All company staff will be blocked from signing in and the public form link will stop until the company is reactivated.'),
    danger: true,
  },
  activate: { label: tr('إعادة التفعيل', 'Reactivate'), text: tr('سيتمكن موظفو الشركة من الدخول ويعود الرابط العام للعمل.', 'Company staff will be able to sign in and the public link will work again.') },
  archive: {
    label: tr('أرشفة الشركة', 'Archive company'),
    text: tr('الشركة المؤرشفة تختفي من القوائم ويتوقف كل شيء. تبقى البيانات محفوظة.', 'An archived company disappears from lists and everything stops. Its data is kept.'),
    danger: true,
  },
};

export function OrganizationDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const [editing, setEditing] = useState(false);
  const [action, setAction] = useState<Action | null>(null);
  const [reason, setReason] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const org = useQuery({ queryKey: ['organization', id], queryFn: () => api<OrganizationDetails>(`/organizations/${id}`) });
  const forms = useQuery({ queryKey: ['forms'], queryFn: () => api<{ items: FormSummary[] }>('/forms') });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['organization', id] });
    void qc.invalidateQueries({ queryKey: ['organizations'] });
  };
  const doAction = useMutation({
    mutationFn: (a: Action) => api(`/organizations/${id}/${a}`, { method: 'POST', body: reason ? { reason } : {} }),
    onSuccess: (_, a) => {
      toast(tr(`تم: ${ACTIONS[a].label}`, `Done: ${ACTIONS[a].label}`));
      setAction(null);
      setReason('');
      refresh();
    },
    onError: toastError,
  });
  const resetPw = useMutation({
    mutationFn: () => api(`/organizations/${id}/owner/reset-password`, { method: 'POST', body: { newPassword } }),
    onSuccess: () => {
      toast(tr('تم تعيين كلمة مرور مؤقتة للمالك', 'Temporary owner password set'));
      setResetOpen(false);
      setNewPassword('');
    },
    onError: toastError,
  });
  const setForm = useMutation({
    mutationFn: (formId: string | null) => api(`/organizations/${id}/form`, { method: 'PUT', body: { formId } }),
    onSuccess: () => {
      toast(tr('تم تحديث النموذج المستخدم', 'Assigned form updated'));
      refresh();
    },
    onError: toastError,
  });

  if (org.isLoading) return <Loading />;
  if (org.isError || !org.data) return <ErrorState error={org.error} onRetry={() => org.refetch()} />;
  const o = org.data;
  const st = ORG_STATUS[o.status];
  const linkLive = o.public_form_enabled && o.status === 'ACTIVE';

  return (
    <>
      <PageActions>
        <Button variant="default" leftSection={<IconPencil size={16} />} onClick={() => setEditing(true)}>{tr('تعديل', 'Edit')}</Button>
        <Menu position="bottom-end" width={220}>
          <Menu.Target>
            <ActionIcon variant="default" size={36} aria-label={tr('إجراءات الشركة', 'Company actions')}><IconDots size={18} /></ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {o.status === 'ACTIVE' && <Menu.Item leftSection={<IconPlayerPause size={16} />} onClick={() => setAction('suspend')}>{tr('تعليق الشركة', 'Suspend company')}</Menu.Item>}
            {o.status !== 'ACTIVE' && <Menu.Item leftSection={<IconPlayerPlay size={16} />} onClick={() => setAction('activate')}>{tr('إعادة التفعيل', 'Reactivate')}</Menu.Item>}
            <Menu.Item leftSection={<IconKey size={16} />} onClick={() => setResetOpen(true)}>{tr('كلمة مرور مؤقتة للمالك', 'Temporary owner password')}</Menu.Item>
            {o.status !== 'ARCHIVED' && (
              <>
                <Menu.Divider />
                <Menu.Item color="red" leftSection={<IconArchive size={16} />} onClick={() => setAction('archive')}>{tr('أرشفة الشركة', 'Archive company')}</Menu.Item>
              </>
            )}
          </Menu.Dropdown>
        </Menu>
      </PageActions>

      <Card mb="lg" padding="lg">
        <Group gap="md" wrap="nowrap" align="center">
          <OrgLogo size={56}>{o.name[0]}</OrgLogo>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" wrap="wrap">
              <Title order={2} fz={20} lineClamp={1}>{o.name}</Title>
              <StatusBadge tone={toneOf(st?.color)}>{st?.label}</StatusBadge>
              <SubscriptionChip status={o.subscriptionStatus} daysLeft={o.daysLeft} planName={o.plan ? nameOf(o.plan) : undefined} access={o.access} />
            </Group>
            {o.name_en
              ? <Text size="sm" c="dimmed" dir="ltr" ta="start">{o.name_en}</Text>
              : <Text size="xs" c="orange.7">{tr('لا يوجد اسم إنجليزي — عدّل الشركة لإضافته', 'No English name — edit the company to add one')}</Text>}
            <Group gap="md" mt={6}>
              <HealthDot health={o.health} withLabel />
              <Text size="xs" c="dimmed">{tr('آخر دخول', 'Last login')}: {fmtAgo(o.lastLoginAt)}</Text>
              <Text size="xs" c="dimmed">{tr('أُنشئت', 'Created')}: {fmtDate(o.created_at)}</Text>
            </Group>
          </Box>
        </Group>
      </Card>

      <Grid gap="lg">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Stack gap="lg">
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
              <Kpi label={tr('العقارات', 'Properties')} value={fmtNum(o.counts.PROPERTY ?? 0)} />
              <Kpi label={tr('الطلبات', 'Requests')} value={fmtNum(o.counts.REQUEST ?? 0)} />
              <Kpi label={tr('الحركة آخر 30 يوماً', 'Activity, last 30 days')} value={fmtNum(o.activity30d ?? 0)} />
            </SimpleGrid>

            <SubscriptionCard org={o} />

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              <Panel title={tr('بيانات الشركة', 'Company details')}>
                <Field label={tr('الرابط (slug)', 'Link (slug)')}><span dir="ltr">/{o.slug}</span></Field>
                <Field label={tr('الهاتف', 'Phone')}><span dir="ltr">{o.phone}</span></Field>
                <Field label={tr('البريد', 'Email')}><span dir="ltr">{o.email}</span></Field>
                <Field label={tr('المنطقة الزمنية', 'Time zone')}><span dir="ltr">{o.timezone}</span></Field>
                <Field label={tr('الرابط العام', 'Public link')}>{o.public_form_enabled ? tr('مفعّل', 'Enabled') : tr('موقوف', 'Disabled')}</Field>
              </Panel>
              <Panel
                title={tr('المالك', 'Owner')}
                actions={<Button size="xs" variant="default" leftSection={<IconKey size={14} />} onClick={() => setResetOpen(true)}>{tr('كلمة مرور مؤقتة', 'Temp password')}</Button>}
              >
                {o.owner ? (
                  <>
                    <Field label={tr('الاسم', 'Name')}>{o.owner.full_name}</Field>
                    <Field label={tr('اسم المستخدم', 'Username')}><span dir="ltr">{o.owner.username}</span></Field>
                    <Field label={tr('البريد', 'Email')}><span dir="ltr">{o.owner.email ?? '—'}</span></Field>
                    <Field label={tr('آخر دخول', 'Last login')}>{fmtDate(o.owner.last_login_at)}</Field>
                  </>
                ) : (
                  <Text size="sm" c="dimmed">{tr('لا يوجد مالك', 'No owner')}</Text>
                )}
              </Panel>
            </SimpleGrid>

            <Panel title={tr('النموذج المستخدم', 'Assigned form')} description={tr('الافتراضي هو النموذج العام الذي تديره من منشئ النموذج', 'The default is the global form you manage in the form builder')}>
              <Select
                maw={420}
                value={o.formId ?? ''}
                onChange={(v) => setForm.mutate(v || null)}
                disabled={setForm.isPending}
                data={[
                  { value: '', label: tr('النموذج الافتراضي', 'Default form') },
                  ...(forms.data?.items ?? [])
                    .filter((f) => !f.is_default && (f.scope === 'GLOBAL' || f.organization_id === o.id))
                    .map((f) => ({ value: f.id, label: `${f.name}${!f.published_version_id ? tr(' (غير منشور)', ' (unpublished)') : ''}`, disabled: !f.published_version_id })),
                ]}
              />
            </Panel>

            <OrgMapSettings orgId={o.id} countryId={o.default_country_id} value={o.map} />
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Stack gap="lg">
            <Panel title={tr('رابط النموذج العام', 'Public form link')}>
              <Stack align="center" gap="md">
                <Box p="md" style={{ border: '1px solid var(--pf-line)', borderRadius: 12, background: '#fff', opacity: linkLive ? 1 : 0.35 }}>
                  <QRCodeSVG value={o.publicUrl} size={164} />
                </Box>
                <Group gap={4} wrap="nowrap" maw="100%">
                  <Text size="sm" dir="ltr" ff="monospace" truncate>{o.publicUrl}</Text>
                  <CopyButton value={o.publicUrl}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? tr('تم النسخ', 'Copied') : tr('نسخ', 'Copy')}>
                        <ActionIcon onClick={copy} color={copied ? 'teal' : 'gray'} aria-label={tr('نسخ', 'Copy')}>{copied ? <IconCheck size={16} /> : <IconCopy size={16} />}</ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                  <Tooltip label={tr('فتح', 'Open')}><ActionIcon component="a" href={o.publicUrl} target="_blank" aria-label={tr('فتح', 'Open')}><IconExternalLink size={16} /></ActionIcon></Tooltip>
                </Group>
                {!linkLive && <Alert color="orange" variant="light" py={6} w="100%">{tr('الرابط لا يعمل حالياً', 'The link is not live right now')}</Alert>}
              </Stack>
            </Panel>
            {o.slugHistory.length > 0 && (
              <Panel title={tr('روابط سابقة', 'Previous links')} description={tr('تُحوَّل تلقائياً للرابط الحالي', 'Redirected to the current link')}>
                {o.slugHistory.map((h) => (
                  <Field key={h.old_slug} label={<span dir="ltr">/{h.old_slug}</span>}><Text size="xs" c="dimmed">{fmtDate(h.changed_at)}</Text></Field>
                ))}
              </Panel>
            )}
          </Stack>
        </Grid.Col>
      </Grid>

      <OrganizationFormDialog open={editing} onClose={() => setEditing(false)} org={o} />
      {action && (
        <ConfirmModal open title={ACTIONS[action].label} text={ACTIONS[action].text} danger={ACTIONS[action].danger} busy={doAction.isPending} onClose={() => setAction(null)} onConfirm={() => doAction.mutate(action)}>
          {action !== 'activate' && <TextInput label={tr('السبب (اختياري)', 'Reason (optional)')} value={reason} onChange={(e) => setReason(e.currentTarget.value)} />}
        </ConfirmModal>
      )}
      <ConfirmModal
        open={resetOpen}
        title={tr('كلمة مرور مؤقتة للمالك', 'Temporary owner password')}
        text={tr('سيُطلب من المالك تغييرها عند الدخول، وستُلغى جلساته الحالية.', 'The owner will be asked to change it on login, and their current sessions will be revoked.')}
        busy={resetPw.isPending}
        onClose={() => setResetOpen(false)}
        onConfirm={() => resetPw.mutate()}
        confirmLabel={tr('تعيين', 'Set')}
      >
        <PasswordInput label={tr('كلمة المرور الجديدة', 'New password')} dir="ltr" value={newPassword} onChange={(e) => setNewPassword(e.currentTarget.value)} description={tr('8 أحرف على الأقل، حروف وأرقام', 'At least 8 characters, letters and numbers')} />
      </ConfirmModal>
    </>
  );
}
